/**
 * The outcome toast, shadcn-styled, keeping the ToastBar contract (nav/ActionDialog.tsx):
 * one message, a tone, `onDone` after four seconds, role status, testid toast-bar.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Why not Sonner (shadcn's current toast): Sonner is imperative (toast("...")) and lives
 * in one app-level <Toaster />; adopting it changes how every page holds its outcome
 * state, which is logic, not presentation. The fleet decides that once, app-wide.
 */
import { CircleAlert, CircleCheck, XIcon } from "lucide-react";
import { useEffect } from "react";

import { strings } from "@/brand/strings";
import { cn } from "@/lib/utils";

export function Toast({
  message,
  tone = "info",
  onDone,
}: {
  readonly message: string;
  readonly tone?: "info" | "danger";
  readonly onDone: () => void;
}): React.JSX.Element {
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, [onDone]);
  const Icon = tone === "danger" ? CircleAlert : CircleCheck;
  return (
    <div
      className={cn(
        "q-ui fixed inset-x-3 top-3 z-50 mx-auto flex max-w-[34rem] items-start gap-3 rounded-md border border-rule-strong",
        "border-s-[3px] bg-card p-3 text-sm text-foreground shadow-xl animate-in fade-in-0 slide-in-from-top-2",
        tone === "danger" ? "border-s-danger" : "border-s-sig",
      )}
      role="status"
      data-testid="toast-bar"
    >
      <Icon
        aria-hidden="true"
        className={cn("mt-0.5 size-5", tone === "danger" ? "text-danger" : "text-sig")}
      />
      <p className="flex-1 leading-normal">{message}</p>
      <button
        type="button"
        aria-label={strings.common.close}
        className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
        onClick={onDone}
      >
        <XIcon className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
